import React, { Component } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { parse } from 'query-string';
import dayjs from 'utils/dayjs';

import {
  getCurrencyCase,
  getCurrencyCaseByValue,
  DEFAULT_SEO_DATE_FORMAT,
  parseDate,
} from 'utils';

const getDisplayName = WrappedComponent => {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component';
};

export const withSeoData = WrappedComponent => {
  class WithSeoData extends Component {
    constructor(props) {
      super(props);

      this.defaultSeo = {
        metaTitle: props.intl.formatMessage({
          id: 'homePage.converter.metaTitle',
        }),
        metaDescription: props.intl.formatMessage({
          id: 'homePage.converter.metaDescription',
        }),
        title: props.intl.formatMessage({
          id: 'homePage.converter.title',
        }),
        allRatesTitle: props.intl.formatMessage({ id: 'allRatesTitle' }),
        allBanksTitle: props.intl.formatMessage({ id: 'allBanksTitle' }),
      };

      this.cachedMatch = null;
      this.cachedSeoData = null;
    }

    getIds = () => {
      const { match } = this.props;

      return [
        'metaTitle',
        'metaDescription',
        'title',
        'allRatesTitle',
        'allBanksTitle',
        ...(match.params.val1
          ? [
              'landingReverseLink',
              'landingDescription1',
              'landingDescription2',
              'landingDescription3',
            ]
          : []),
      ].map(id => `${match.params.val1 ? 'vl' : 'cl'}.${id}`);
    };

    getAmounts = () => {
      const {
        source,
        target,
        sourceRate,
        targetRate,
        match: {
          params: { val1 },
        },
        intl,
      } = this.props;

      if (!val1) {
        return {};
      }

      return {
        amountS: sourceRate
          ? intl.formatNumber(parseInt(val1, 10) * sourceRate, {
              maximumFractionDigits: target.type === 'crypto' ? 8 : 2,
            })
          : '—',
        amountT: targetRate
          ? intl.formatNumber(parseInt(val1, 10) * targetRate, {
              maximumFractionDigits: source.type === 'crypto' ? 8 : 2,
            })
          : '—',
      };
    };

    getLocationValues = () => {
      const { location, intl, existedConverterType } = this.props;

      const { date } = parse(location.search);

      return {
        date: (parseDate(date) || dayjs())
          .locale(intl.locale)
          .format(DEFAULT_SEO_DATE_FORMAT),
        converterType: intl.formatMessage({
          id: `currencyConverter.converterType.${existedConverterType ||
            'midbank'}`,
        }),
      };
    };

    getIntlValues = () => {
      const {
        source,
        target,
        match: {
          params: { val1 },
        },
        intl,
      } = this.props;

      const { name: sourceName, code: sourceCode } = source;
      const { name: targetName, code: targetCode } = target;

      return {
        ...this.getAmounts(),
        ...this.getLocationValues(),
        value: val1,
        formattedValue: val1 && intl.formatNumber(val1),
        currencyS: sourceName,
        currencyT: targetName,
        currencyCodeS: sourceCode,
        currencyCodeT: targetCode,
        ...getCurrencyCaseByValue(source, target, val1),
        ...getCurrencyCaseByValue(source, target, val1, 'acc'),
        ...getCurrencyCaseByValue(source, target, val1, 'g'),
        ...getCurrencyCase(source, target, 'acc'),
        ...getCurrencyCase(source, target, 'g'),
        ...getCurrencyCase(source, target, 'gp'),
        ...getCurrencyCase(source, target, 'mnps'),
        ...getCurrencyCase(source, target, 'p'),
      };
    };

    getSeoData = () => {
      const { match, source, target, intl } = this.props;

      if (
        !source.code ||
        !target.code ||
        !match.params.sourceCurrency ||
        !match.params.targetCurrency
      ) {
        return this.defaultSeo;
      }

      const ids = this.getIds();

      const values = this.getIntlValues();

      return ids.reduce(
        (result, id) => ({
          ...result,
          [id.split('.')[1]]: intl.formatMessage(
            {
              id,
            },
            values
          ),
        }),
        {}
      );
    };

    render() {
      const seoData = this.getSeoData();

      return <WrappedComponent {...this.props} {...seoData} />;
    }
  }

  WithSeoData.displayName = `withSeoData(${getDisplayName(WrappedComponent)})`;

  hoistNonReactStatics(withSeoData, WrappedComponent);

  WithSeoData.propTypes = {
    source: PropTypes.objectOf(PropTypes.string).isRequired,
    target: PropTypes.objectOf(PropTypes.string).isRequired,
    existedConverterType: PropTypes.string,
    sourceRate: PropTypes.number,
    targetRate: PropTypes.number,

    location: PropTypes.shape({
      search: PropTypes.string,
    }).isRequired,
    match: PropTypes.shape({
      params: PropTypes.shape({
        val1: PropTypes.string,
        sourceCurrency: PropTypes.string,
        targetCurrency: PropTypes.string,
      }),
    }).isRequired,

    intl: PropTypes.shape({
      locale: PropTypes.string,
      formatMessage: PropTypes.func,
      formatNumber: PropTypes.func,
    }).isRequired,
  };

  WithSeoData.defaultProps = {
    existedConverterType: null,
    sourceRate: null,
    targetRate: null,
  };

  return WithSeoData;
};

export default withSeoData;
